/**
 *  Menu
 *    Scripts to control menu functionality on the site, mainly used for mobile.
 *
 *  Version:
 *    1.0.0 - 2022/07/08
 */

import { responsiveMediaAbove } from "../../settings/responsive";
import { CLASS_MENU_HOVER, CLASS_SEARCH_OPEN } from "../../tools/constants/classes";
import { documentDisableScroll, documentEnableScroll } from "../../tools/document";

export const SELECTOR_MENU = '[data-main-menu]';
const SELECTOR_SUBMENU = '[data-menu-submenu]';
const SELECTOR_MENU_TOGGLE = '[data-menu-toggle]';
const SELECTOR_MENU_CLOSE = '[data-menu-close]';
const SELECTOR_SUBMENU_TOGGLE = '[data-submenu-toggle]';
const SELECTOR_MENU_BACK = '[data-submenu-back]';

const ATTR_SUBMENU = 'data-menu-submenu';

const CLASS_MENU_OPEN = 'is-menu-open';
const CLASS_SUBMENU_OPEN = 'is-submenu-open';
const CLASS_MENU_LEVEL = 'is-menu-level-';

export class Menu {
  container:HTMLElement;
  header:HTMLElement;
  submenus:NodeListOf<HTMLUListElement> | null = null;
  menuLevel:number = 1;
  currentMenu:HTMLElement | null = null;

  constructor(container:HTMLElement, header:HTMLElement) {
    this.container = container;
    this.header = header;
    this.queryChildren(container);
    this.attachEvents();
  }

  queryChildren(container: HTMLElement) {
    this.submenus = container.querySelectorAll(SELECTOR_SUBMENU);
  }

  attachEvents() {

    document.querySelectorAll<HTMLButtonElement>(SELECTOR_MENU_TOGGLE)
    .forEach(element => {
      element.addEventListener('click', this.onMenuToggle);
    });

    document.querySelectorAll<HTMLButtonElement>(SELECTOR_MENU_CLOSE)
    .forEach(element => {
      element.addEventListener('click', this.closeMenu);
    });

    this.container.querySelectorAll<HTMLAnchorElement>(SELECTOR_SUBMENU_TOGGLE)
    .forEach(element => {
      element.addEventListener('click', this.onSubmenuOpen);
    });

    this.container.querySelectorAll<HTMLAnchorElement>(SELECTOR_MENU_BACK)
    .forEach(element => {
      element.addEventListener('click', this.onSubmenuBack);
    });

    // Use these events if you need to change styling on other elements on hover
    this.container.addEventListener('mouseover', this.onMouseOver);
    this.container.addEventListener('mouseout', this.onMouseOut);
  }

  /*
    Events
  */
  onMenuToggle = (e:Event) => {
    e.preventDefault();
    if(responsiveMediaAbove('medium')) {
      this.container.classList.remove(CLASS_MENU_OPEN);
      this.header.classList.remove(CLASS_MENU_HOVER, CLASS_SEARCH_OPEN);
    } else {
      if(this.container.classList.contains(CLASS_MENU_OPEN)) return this.closeMenu();
      this.openMenu();
    }
  }

  onSubmenuOpen = (e:Event) => {
    e.preventDefault();

    //Finding the child submenu of the link clicked on
    const thisTarget = e.currentTarget as HTMLElement;
    const newSubmenu = thisTarget?.parentElement?.querySelector<HTMLUListElement>(SELECTOR_SUBMENU);
  
    if(!newSubmenu || !newSubmenu.getAttribute(ATTR_SUBMENU)) {
      return console.warn('Error: Menu structure has changed.');
    }

    this.updateSubmenuClasses(null, newSubmenu);
    documentDisableScroll();
  }

  onSubmenuBack = (e:Event) => {
    e.preventDefault();

    //Element you've clicked on
    const parentElement = this.currentMenu?.closest('li');
    const newSubmenu = parentElement?.closest<HTMLElement>(SELECTOR_SUBMENU);

    if(!newSubmenu) return console.warn('Error: Menu structure has changed.');

    this.updateSubmenuClasses(this.currentMenu!, newSubmenu!);
  }

  onMouseOver = () => {
    if(!this.header.classList.contains(CLASS_MENU_HOVER)) {
      this.header.classList.add(CLASS_MENU_HOVER);
    }
  }

  onMouseOut = () => {
    this.header.classList.remove(CLASS_MENU_HOVER);
  }

  /*
    Methods
  */

  //Closing all submenus and hiding the drawer
  closeMenu = () => {
    this.container.classList.remove(CLASS_MENU_OPEN);
    this.header.classList.remove(CLASS_MENU_OPEN, CLASS_MENU_HOVER);
    this.resetSubmenuClasses();
    this.updateMenuStateClasses(1);
    documentEnableScroll();
  }

  openMenu = () => {
    this.container.classList.add(CLASS_MENU_OPEN);
    this.header.classList.add(CLASS_MENU_OPEN);
    documentDisableScroll();
  }

  resetSubmenuClasses = () => {
    this.container.querySelectorAll<HTMLElement>(SELECTOR_SUBMENU).forEach(submenu => {
      submenu.classList.remove(CLASS_SUBMENU_OPEN);
    });
  }

  updateSubmenuClasses = (thisSubmenu:HTMLElement | null, newSubmenu:HTMLElement) => {
    const newLevel = parseInt(newSubmenu.getAttribute(ATTR_SUBMENU) || '');
    if(!newLevel || !this.submenus) return;
    thisSubmenu?.classList.remove(CLASS_SUBMENU_OPEN);

    Array.from(this.submenus).forEach(menu => {
      const equalNode = menu.isEqualNode(newSubmenu);
      //Close other submenus and open active one on level 3
      if(equalNode) {
        menu.classList.add(CLASS_SUBMENU_OPEN);
      } else {
        menu.classList.remove(CLASS_SUBMENU_OPEN);
      }
    });

    this.currentMenu = newSubmenu;

    this.updateMenuStateClasses(newLevel);
  }

  updateMenuStateClasses = (newLevel:number)  => { 
    this.container.classList.remove(`${CLASS_MENU_LEVEL}${this.menuLevel}`);
    this.container.classList.add(`${CLASS_MENU_LEVEL}${newLevel}`);
    this.menuLevel = newLevel;

    if(responsiveMediaAbove('medium') && this.header) {
      if(!this.header.classList.contains(CLASS_MENU_OPEN) && newLevel > 1) {
        this.header.classList.add(CLASS_MENU_OPEN);
      } else if(newLevel == 1) {
        this.header.classList.remove(CLASS_MENU_OPEN);
      }
    }
  }
}